/**
 * Entity avatar utilities.
 *
 * Resolution order (best → worst):
 *   1. `entity.metadata.thumbnail` — real Wikipedia photo (89% of seed data)
 *   2. Gender-aware DiceBear cartoon, biased by entity.metadata.sex
 *   3. Initials avatar as the last-ditch fallback
 *
 * Component code should pass the whole `entity` object when possible —
 * `getAvatarSource(entity)` does the right thing automatically.
 */
const DICEBEAR_BASE = 'https://api.dicebear.com/7.x';

// Per-category DiceBear style. Pick styles that have visibly distinct
// male / female variants when the seed differs.
const STYLE_MAP = {
  country_leader: 'personas',
  ceo:            'personas',
  actor:          'adventurer',
  athlete:        'avataaars',     // Cleaner gendered shapes than `bottts`
  musician:       'adventurer',
  influencer:     'avataaars',
};

export const AVATAR_GRADIENTS = {
  country_leader: 'linear-gradient(135deg, #5a2d5e 0%, #8b5e8e 100%)',
  ceo:            'linear-gradient(135deg, #1e3a5f 0%, #4a7ab5 100%)',
  actor:          'linear-gradient(135deg, #c97a2e 0%, #e6a84e 100%)',
  athlete:        'linear-gradient(135deg, #2d7d5e 0%, #5cb88a 100%)',
  musician:       'linear-gradient(135deg, #b8860b 0%, #daa520 100%)',
  influencer:     'linear-gradient(135deg, #c94070 0%, #e87898 100%)',
};

export const AVATAR_COLORS = {
  country_leader: '#5a2d5e',
  ceo:            '#1e3a5f',
  actor:          '#c97a2e',
  athlete:        '#2d7d5e',
  musician:       '#b8860b',
  influencer:     '#c94070',
};

/** Seed prefix biases DiceBear's PRNG toward a gendered feature set. */
function genderSeed(name, sex) {
  if (!sex || sex === 'non-binary') return name;
  return `${sex === 'female' ? 'f' : 'm'}_${name}`;
}

/**
 * Generate a DiceBear avatar URL for an entity.
 * @param {string} name
 * @param {string} entityType
 * @param {number} size
 * @param {?string} sex  'male' | 'female' | 'non-binary' — biases the seed
 *                       so masculine/feminine features differ.
 */
export function getEntityAvatarUrl(name, entityType, size = 64, sex = null) {
  const style = STYLE_MAP[entityType] || 'initials';
  const seed = encodeURIComponent(genderSeed(name || 'unknown', sex));
  return `${DICEBEAR_BASE}/${style}/svg?seed=${seed}&size=${size}&backgroundColor=transparent`;
}

export function getInitialsAvatarUrl(name, size = 64) {
  const seed = encodeURIComponent(name || 'unknown');
  return `${DICEBEAR_BASE}/initials/svg?seed=${seed}&size=${size}&backgroundColor=transparent`;
}

/**
 * Decide which image to render for an entity. Returns the URL plus
 * a `sourceKind` so the component can show different alt-text or styles
 * for real photos vs cartoons.
 *
 *   { src: 'https://.../Taylor_Swift.png?width=512', sourceKind: 'real' }
 *   { src: 'https://api.dicebear.com/...',           sourceKind: 'dicebear' }
 */
export function getAvatarSource(entity, size = 64) {
  const thumb = entity?.metadata?.thumbnail || entity?.thumbnail;
  if (thumb) return { src: thumb, sourceKind: 'real' };
  return {
    src: getEntityAvatarUrl(
      entity?.name,
      entity?.entity_type || entity?.entityType,
      size,
      entity?.metadata?.sex || entity?.sex,
    ),
    sourceKind: 'dicebear',
  };
}

/**
 * True when the entity's CURRENTLY-DISPLAYED portrait was generated by AI
 * (Nano Banana). Drives the in-app "AI-generated" transparency badge so we
 * disclose synthetic assets to users (Valve/Steam-style disclosure norms).
 * The canonical signal is the `/generated_avatars/` path segment that the
 * portrait pipeline writes for AI variants only.
 */
export function isAiGeneratedPortrait(entity) {
  const thumb = entity?.metadata?.thumbnail || entity?.thumbnail;
  return typeof thumb === 'string' && thumb.includes('/generated_avatars/');
}

export function getAvatarProps(entity) {
  const { src } = getAvatarSource(entity);
  return {
    src,
    fallbackSrc: getInitialsAvatarUrl(entity?.name),
    bgColor: AVATAR_COLORS[entity?.entity_type] || '#666',
    bgGradient: AVATAR_GRADIENTS[entity?.entity_type] || AVATAR_GRADIENTS.athlete,
    initial: entity?.name?.charAt(0) || '?',
  };
}

/**
 * Team/USER row avatar (leaderboard rows, mini-standings, chat). Prefers the
 * freshest AuthContext copy (`me`) for the current user so a just-changed
 * avatar shows immediately. Resolution: uploaded photo → chosen DiceBear →
 * initials image (never a bare letter/blank). Mirrors mobile getTeamAvatarUrl.
 */
export function buildTeamRowAvatar(row, me, size = 64) {
  const src = (me && row?.user_id === me.id) ? me : row;
  if (src?.avatar_type === 'photo' && src?.avatar_url) {
    return src.avatar_url.startsWith('http')
      ? src.avatar_url
      : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${src.avatar_url}`;
  }
  if (src?.avatar_type === 'dicebear' && src?.avatar_style && src?.avatar_seed) {
    return `${DICEBEAR_BASE}/${src.avatar_style}/svg?seed=${encodeURIComponent(src.avatar_seed)}&size=${size}&backgroundColor=transparent`;
  }
  return getInitialsAvatarUrl(row?.username || row?.name || '?', size);
}
